import { useEffect } from 'react'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { NoticePageProps } from '@/features/applicant/types/pages'

export function NoticePage({ title, content, linkType }: NoticePageProps) {
  const navigate = useNavigate()

  // Notify parent window when page is loaded
  useEffect(() => {
    // Send message to parent window that this link was opened
    if (window.opener) {
      window.opener.postMessage(
        { type: 'linkOpened', linkType },
        window.location.origin
      )
    }
  }, [linkType])

  // Handle close button - notify parent and close window
  const handleClose = () => {
    // Send message to parent window that this link was closed
    if (window.opener) {
      window.opener.postMessage(
        { type: 'linkClosed', linkType },
        window.location.origin
      )
    }
    
    // Close the window
    window.close()
    
    // Fallback: if window.close() doesn't work (some browsers block it), navigate back
    // This won't work if opened in new tab, but will work if opened in popup
    if (!document.hidden) {
      // If window didn't close, try to navigate back (only works if in same window)
      try {
        window.history.back()
      } catch {
        // If that fails, redirect to signature page
        navigate('/applicant/application/signature')
      }
    }
  }

  // Listen for beforeunload to notify parent when window is closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (window.opener) {
        window.opener.postMessage(
          { type: 'linkClosed', linkType },
          window.location.origin
        )
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [linkType])

  return (
    <div data-testid={`applicant-notice-page-${linkType}`} className="min-h-screen bg-gray-50">
      <style>{`
        .notice-content a { color: #2563eb; text-decoration: underline; }
        .notice-content a:hover { color: #1d4ed8; }
        /* Restore list and formatting (Tailwind preflight resets these) */
        .notice-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.75rem 0; }
        .notice-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.75rem 0; }
        .notice-content li { display: list-item; margin: 0.25rem 0; }
        .notice-content ul ul, .notice-content ol ul { list-style-type: circle; padding-left: 1.5rem; }
        .notice-content ul ol, .notice-content ol ol { list-style-type: lower-alpha; padding-left: 1.5rem; }
        .notice-content h2 { font-size: 1.25rem; font-weight: 700; margin: 1rem 0 0.5rem; }
        .notice-content h3 { font-size: 1.125rem; font-weight: 600; margin: 0.75rem 0 0.5rem; }
        .notice-content p { margin: 0.5rem 0; }
        .notice-content strong, .notice-content b { font-weight: 700; }
      `}</style>
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          <button
            data-testid={`applicant-notice-${linkType}-header-close-button`}
            onClick={handleClose}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div
          data-testid={`applicant-notice-${linkType}-content`}
          className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6 md:p-8"
        >
          {typeof content === 'string' ? (
            <div
              className="notice-content prose prose-sm max-w-none"
              style={{ wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div className="prose prose-sm max-w-none notice-content">{content}</div>
          )}
        </div>
        <div className="mt-6 flex justify-center">
          <button
            data-testid={`applicant-notice-${linkType}-footer-close-button`}
            onClick={handleClose}
            className="px-6 py-3 bg-[#1597ff] hover:bg-[#0d7acc] text-white rounded-lg transition-colors font-medium"
          >
            Close and Return
          </button>
        </div>
      </div>
    </div>
  )
}

